/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, SanityRichText, Flex, Box } from '@tishman/components';
import { graphql } from 'gatsby';
import React, { useMemo } from 'react';

import { ArticleSchema } from '../../../components/schema-dot-org/ArticleSchema';
import { blockContentToString } from '../../../utils/blockContentToString';
import { Layout } from '../../../layouts';
import StoryHeroBlock from '../../../blocks/StoryHeroBlock';
import StoryShareBlock from '../../../blocks/StoryShareBlock';
import StoryWideCtaBlock from '../../../blocks/StoryWideCtaBlock';
import StoryRelatedStoriesBlock from '../../../blocks/StoryRelatedStoriesBlock';
import ShareYourExperienceBlock from '../../../blocks/ShareYourExperienceBlock';
import { storyComponents } from '../../../components/StoryComponents';
import StoryBuyTicketsBlock from '../../../blocks/StoryBuyTicketsBlock';
import StoryFiltersListBlock from '../../../blocks/StoryFiltersListBlock';
import StoryFeatureStoriesBlock from '../../../blocks/StoryFeatureStoriesBlock';
import StoryEditorialBlock from '../../../blocks/StoryEditorialBlock';

import type { PageConfig } from '../../../PageConfig';

export const pageConfig: PageConfig = {
  theme: 'Rock Center Green',
  pageName: 'The Center Magazine',
  cta: {
    to: '/the-center-newsletter/',
    label: 'Subscribe',
  },
};

const StoryTemplate = (props: {
  data: Queries.SanityStoryQuery;
  path: string;
}): JSX.Element => {
  const { data, path } = props;

  const imageUrl =
    data.story?.poster?.asset?.gatsbyImageData?.images?.fallback?.src ?? '';
  const author = data.story?.authors?.[0] ?? '';

  const storyCategoriesProps = useMemo(() => {
    if (!data.storyJson?.storyCategories)
      throw new Error('Expected story categories');
    return {
      storyCategories: data.storyJson?.storyCategories,
    };
  }, [data]);

  const buyTicketsProps = useMemo(() => {
    if (!data.buyTicketsJson?.buyTicketsBlock)
      throw new Error('Expected story buy tickets data');
    return {
      buyTicketsBlock: data.buyTicketsJson?.buyTicketsBlock,
    };
  }, [data]);

  return (
    <Layout theme="Rock Center Green">
      <StoryHeroBlock data={data} id="story-hero" />
      <Section id="story-story" theme="Rock Center">
        <Flex
          sx={{
            width: '100%',
            maxWidth: 1440,
            margin: '0 auto',
            justifyContent: 'flex-start',
            position: 'relative',
          }}
        >
          <Box
            as="div"
            sx={{
              display: ['none', null, null, null, null, 'block'],
              ml: '56px',
              mr: '86px',
            }}
          >
            <Flex
              sx={{
                'maxWidth': 320,
                'flexDirection': 'column',
                '> *:not(:last-child)': { borderBottom: '2px solid #D8D8D8' },
              }}
            >
              <StoryShareBlock
                data={data}
                id="story-share"
                isLeftSideMenu={true}
                theme="Rock Center"
              />
              <StoryFiltersListBlock
                items={storyCategoriesProps.storyCategories}
              />
              <StoryBuyTicketsBlock
                buyTicketsBlock={buyTicketsProps.buyTicketsBlock}
              />
              <StoryFeatureStoriesBlock
                data={data}
                id="story-featured-desktop-stories"
              />
            </Flex>
          </Box>
          <Box
            as="div"
            sx={{
              width: '100%',
              maxWidth: 900,
              pb: 5,
              pt: 5,
              margin: '0 auto',
            }}
          >
            {data?.story?._rawBody && Array.isArray(data.story._rawBody) && (
              <SanityRichText
                blocks={data.story._rawBody}
                components={storyComponents}
              />
            )}
          </Box>
        </Flex>
        <Flex
          sx={{
            width: '100%',
            maxWidth: 1440,
            margin: '0 auto',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <StoryShareBlock
            data={data}
            id="story-share"
            showBack={true}
            theme="Rock Center"
          />
        </Flex>
      </Section>

      <Flex
        sx={{
          display: ['flex', null, null, null, null, 'none'],
          flexDirection: 'column',
        }}
      >
        <StoryFiltersListBlock
          isMobile={true}
          items={storyCategoriesProps.storyCategories}
        />
        <StoryRelatedStoriesBlock
          data={data}
          id="story-featured-mobile-stories"
        />
      </Flex>

      {data.story?.editorialArticlesField && (
        <StoryEditorialBlock data={data} theme="Rock Center Night_Green" />
      )}
      <StoryWideCtaBlock data={data} id="story-wide-cta" theme="Rock Center" />
      <ShareYourExperienceBlock
        id="share-your-experience"
        theme="Rock Center Cream"
      />
      {data.story && (
        <ArticleSchema
          articleBody={blockContentToString(
            data?.story?._rawBody && Array.isArray(data.story._rawBody)
              ? data.story._rawBody
              : undefined,
          )}
          author={author || ''}
          description={data.story.excerpt!}
          headline={data.story.titleAndSlug!.title!}
          imageUrl={imageUrl}
          modifiedDate={data.story.publishAt!}
          publishedDate={data.story.publishAt!}
          url={`https://www.rockefellercenter.com${path}`}
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query SanityStory($id: String!) {
    meta: sanityStory(id: { eq: $id }) {
      seo {
        title: metaTitle
        description: metaDescription
        canonical: metaCanonicalUrl
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
      poster {
        asset {
          gatsbyImageData(
            layout: FULL_WIDTH
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
        alt
      }
    }
    story: sanityStory(id: { eq: $id }) {
      titleAndSlug {
        title
        slug {
          current
        }
      }
      category
      excerpt
      authors
      poster {
        asset {
          gatsbyImageData(layout: CONSTRAINED)
        }
        alt
      }
      publishAt
      formattedPublishAt: publishAt(formatString: "MMM D YYYY")
      _rawBody
      featuredStories {
        excerpt
        titleAndSlug {
          title
          slug {
            current
          }
        }
        category
        formattedPublishAt: publishAt(formatString: "MM.D.YY")
        poster {
          alt
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
      }
      editorialArticlesField {
        title
        articles {
          _id
          excerpt
          titleAndSlug {
            title
            slug {
              current
            }
          }
          category
          formattedPublishAt: publishAt(formatString: "MM.D.YY")
          poster {
            alt
            asset {
              gatsbyImageData(layout: CONSTRAINED)
            }
          }
        }
      }
    }
    recentStories: allSanityStory(limit: 2, sort: { publishAt: DESC }) {
      nodes {
        titleAndSlug {
          slug {
            current
          }
          title
        }
        category
        excerpt
        formattedPublishAt: publishAt(formatString: "MMM D YYYY")
        poster {
          alt
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
      }
    }
    featured: sanityStoryLp(_type: { eq: "storyLP" }) {
      featuredStory {
        _id
        excerpt
        titleAndSlug {
          title
          slug {
            current
          }
        }
        category
        formattedPublishAt: publishAt(formatString: "MM.D.YY")
        poster {
          alt
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
      }
    }
    storyJson: dataJson(jsonId: { eq: "story" }) {
      wideCta {
        caption
        title
        link {
          label
          url
        }
      }
      storyCategories
    }
    buyTicketsJson: dataJson(jsonId: { eq: "story-buy-tickets" }) {
      buyTicketsBlock {
        text
        ctaText
        ctaLink
      }
    }
  }
`;
export default StoryTemplate;
